import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';

class AIEducatorService {
  // Get user's subscription
  async getUserSubscription(userId) {
    try {
      const docRef = doc(db, 'aiEducatorSubscriptions', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { success: true, subscription: docSnap.data() };
      }
      
      return { success: false, subscription: null };
    } catch (error) {
      console.error('Error getting subscription:', error);
      return { success: false, error: error.message };
    }
  }

  // Create new subscription after payment
  async createSubscription(userId, planData, paymentData) {
    try {
      const now = new Date();
      const endDate = new Date(now);
      
      // Add days based on billing cycle
      if (planData.billingCycle === 'yearly') {
        endDate.setDate(endDate.getDate() + 365);
      } else if (planData.billingCycle === 'trial') {
        endDate.setDate(endDate.getDate() + 3); // 3 days for trial/Samosa Pack
      } else {
        endDate.setDate(endDate.getDate() + 30);
      }

      const subscriptionData = {
        userId,
        planId: planData.planId,
        planName: planData.planName,
        billingCycle: planData.billingCycle,
        hoursLimit: planData.hours,
        hoursUsed: 0,
        hoursRemaining: planData.hours,
        startDate: serverTimestamp(),
        endDate: endDate,
        status: 'active',
        paymentId: paymentData.paymentId,
        amount: paymentData.amount,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'aiEducatorSubscriptions', userId), subscriptionData);
      
      return { success: true, subscription: subscriptionData };
    } catch (error) {
      console.error('Error creating subscription:', error);
      return { success: false, error: error.message };
    }
  }

  // Track usage (increment hours used)
  async trackUsage(userId, seconds) {
    try {
      const hours = seconds / 3600; // Convert seconds to hours
      
      const docRef = doc(db, 'aiEducatorSubscriptions', userId);
      await updateDoc(docRef, {
        hoursUsed: increment(hours),
        hoursRemaining: increment(-hours),
        lastUsedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error tracking usage:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if subscription is valid and has hours remaining
  async checkSubscriptionValidity(userId) {
    try {
      const result = await this.getUserSubscription(userId);
      
      if (!result.success || !result.subscription) {
        return { 
          isValid: false, 
          reason: 'no_subscription',
          message: 'No active subscription found'
        };
      }

      const sub = result.subscription;
      const now = new Date();
      const endDate = sub.endDate.toDate ? sub.endDate.toDate() : new Date(sub.endDate);

      // Check if expired
      if (now > endDate) {
        // Update status to expired
        await updateDoc(doc(db, 'aiEducatorSubscriptions', userId), {
          status: 'expired',
          updatedAt: serverTimestamp()
        });
        
        return { 
          isValid: false, 
          reason: 'expired',
          message: 'Your subscription has expired',
          subscription: sub
        };
      }

      // Check if hours exhausted
      if (sub.hoursRemaining <= 0) {
        return { 
          isValid: false, 
          reason: 'hours_exhausted',
          message: 'You have used all your hours for this month',
          subscription: sub
        };
      }

      // Check if inactive
      if (sub.status !== 'active') {
        return { 
          isValid: false, 
          reason: 'inactive',
          message: 'Your subscription is not active',
          subscription: sub
        };
      }

      return { 
        isValid: true, 
        subscription: sub 
      };
    } catch (error) {
      console.error('Error checking validity:', error);
      return { 
        isValid: false, 
        reason: 'error',
        message: error.message 
      };
    }
  }

  // Get usage statistics
  async getUsageStats(userId) {
    try {
      const result = await this.getUserSubscription(userId);
      
      if (!result.success || !result.subscription) {
        return { success: false, error: 'No subscription found' };
      }

      const sub = result.subscription;
      const usagePercentage = (sub.hoursUsed / sub.hoursLimit) * 100;
      const daysRemaining = Math.ceil((sub.endDate.toDate() - new Date()) / (1000 * 60 * 60 * 24));

      return {
        success: true,
        stats: {
          hoursUsed: sub.hoursUsed.toFixed(2),
          hoursRemaining: sub.hoursRemaining.toFixed(2),
          hoursLimit: sub.hoursLimit,
          usagePercentage: usagePercentage.toFixed(1),
          daysRemaining,
          planName: sub.planName,
          status: sub.status
        }
      };
    } catch (error) {
      console.error('Error getting usage stats:', error);
      return { success: false, error: error.message };
    }
  }
}

export const aiEducatorService = new AIEducatorService();
export default aiEducatorService;
